import {useEffect, useRef, useState, type RefObject} from 'react';
import {motion} from 'framer-motion';
import styled, {css} from 'styled-components';
import {PiMark} from '../pi/PiMark';
import {PI_ASPECT, PI_CARDINAL} from '../pi/piPaths';

/**
 * The hero title's finale flourish: once the settled title is on screen, the
 * dot of the "i" in "Intelligence" becomes a little cardinal π. The glyph's
 * real tittle is clipped away at the instant the π emerges from the dot's
 * exact spot, and the pop itself is pure squash-and-stretch: the π launches
 * stretched tall, squashes at the top of its spring overshoot, and relaxes
 * into place as the letter's new dot — no rings or particles, just motion.
 * Settled, it keeps an ever-so-slight perpetual bounce so it reads as alive.
 * Given an `onClick`, the π also doubles as a real button — the hero uses
 * this to let a click on the bouncing π replay the whole intro sequence.
 *
 * It finds the exact dot by reading the per-glyph `data-ch` spans that
 * `HeroTitleMorph` renders its settled title with (see `CharLines` there), so
 * the π launches from the real rendered glyph at any viewport size, and
 * re-anchors on resize.
 */

/** The word whose dotted "i" births the π. Located in the settled title's glyph spans. */
const ANCHOR_WORD = 'Intelligence';

/** Offset of the dotted "i" within {@link ANCHOR_WORD}. */
const ANCHOR_CHAR_OFFSET = 'Intell'.length;

// The three fractions below are all measured against the anchor glyph's
// line-box height and were derived from Jost 700's real font metrics
// (canvas `measureText` at the hero's title size): baseline sits at 0.816 of
// the line box, the "i"'s dot top at 0.166, and the x-height (stem top) at
// 0.398. They hold at every viewport size because the title's font and
// line-height are fixed — only the pixel size scales.

/**
 * Where the "i"'s tittle (dot) sits inside the measured character box, as a
 * fraction of the box height from its top.
 */
const DOT_CENTER_FRACTION = 0.23;

/**
 * The tittle's approximate radius as a fraction of the character box height.
 * With {@link DOT_CENTER_FRACTION}, locates the dot's bottom edge (which the
 * clip boundary must clear — see {@link DOT_CLIP_PATH}).
 */
// Documents the geometry the sibling constants derive from and anchors the
// TSDoc links above; not read directly by code.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const DOT_RADIUS_FRACTION = 0.06;

/**
 * Where the resting π's bottom sits below the dot's center, as a fraction of
 * the character box height. A little deeper than the dot's own bottom edge
 * ({@link DOT_RADIUS_FRACTION}) so the π nestles toward the stem rather than
 * hovering, while still clearing the stem's top (~40% — see
 * {@link DOT_CLIP_PATH}).
 */
const REST_BOTTOM_OFFSET_FRACTION = 0.095;

/**
 * Hides the glyph's own tittle once the π takes over as the dot: clips away
 * the top of the "i"'s character box. 34% falls between the dot's bottom
 * (~29% — see {@link DOT_CENTER_FRACTION} / {@link DOT_RADIUS_FRACTION}) and
 * the stem's top (~40%), so the dot vanishes and the stem stays whole.
 */
const DOT_CLIP_PATH = 'inset(34% 0 0 0)';

/**
 * π width as a fraction of the anchor glyph's line-box height — tittle-scale,
 * deliberately "little" next to the headline type.
 */
const PI_SIZE_FRACTION = 0.29;

/**
 * Hold between mount (the caller mounts this when the title settles) and the
 * pop, in ms — a beat of stillness so the pop reads as its own moment.
 */
const POP_DELAY_MS = 500;

/**
 * The pop's coordinate frame: absolutely positioned over the resting spot of
 * the π (the caller's container must be `position: relative`). Decorative by
 * default — it never intercepts the pointer — unless the caller passes
 * `onClick`, in which case it renders as a real replay button (see
 * {@link PiDotPopProps.onClick}) with a hit area padded well beyond the
 * tittle-scale glyph.
 */
const PopLayer = styled.div<{$clickable: boolean}>`
  position: absolute;
  pointer-events: none;

  ${({$clickable}) =>
    $clickable &&
    css`
      pointer-events: auto;
      cursor: pointer;
      /* Strip the native button chrome — the π itself is the whole visual. */
      background: none;
      border: none;
      padding: 0;
      /* The glyph is tiny; extend the clickable area to a forgiving target. */
      &::after {
        content: '';
        position: absolute;
        inset: -10px;
      }
    `}
`;

/** The measured launch point, in px relative to the caller's container. */
interface DotAnchor {
  /** Dot center x. */
  xPx: number;
  /** Dot center y. */
  yPx: number;
  /** The anchor glyph's line-box height — the scale all pop geometry derives from. */
  charHeightPx: number;
}

/**
 * Amplitude of the settled π's idle bounce, as a fraction of the anchor
 * glyph's line-box height — "ever so slight": a couple of pixels at desktop
 * title sizes, just enough to read as alive rather than pinned.
 */
const IDLE_BOUNCE_FRACTION = 0.03;

/** One full up-and-down of the settled π's idle bounce, in seconds. */
const IDLE_BOUNCE_PERIOD_SEC = 2.6;

/**
 * The pop's lifecycle. `waiting` is the beat of stillness after the title
 * settles; `popping` plays the one-shot spring out of the dot; `settled` is
 * the hold state afterward, where the π keeps an ever-so-slight idle bounce.
 */
type PopPhase = 'waiting' | 'popping' | 'settled';

/** Props for {@link PiDotPop}. */
export interface PiDotPopProps {
  /**
   * Ref to the container holding the settled `HeroTitleMorph` title. Must be
   * a positioned ancestor (`position: relative`) — the pop layer is
   * absolutely positioned inside it — and must contain the settled title's
   * `data-ch` glyph spans.
   */
  containerRef: RefObject<HTMLElement | null>;
  /** π color. Omitted defaults to the Stanford cardinal (`PI_CARDINAL`). */
  color?: string;
  /**
   * Makes the π clickable: the pop layer renders as a real `<button>` (with
   * keyboard activation for free) and calls this on activation. The caller
   * decides what a click means — e.g. the hero replays its whole intro
   * sequence. Omitted leaves the π purely decorative: hidden from the
   * accessibility tree and transparent to the pointer.
   */
  onClick?: () => void;
  /**
   * Called once the π has finished popping in and settled into its idle
   * bounce (immediately, under `prefers-reduced-motion`, since there's no
   * pop animation to wait for). Never called more than once per mount.
   */
  onSettled?: () => void;
}

/**
 * Renders the little π that pops out of — and replaces — the dot of the "i"
 * in "Intelligence" (see module comment). Mount it once the title has
 * settled — the pop plays {@link POP_DELAY_MS} after mount. Under
 * `prefers-reduced-motion` the tittle is swapped for the resting π directly,
 * with no ripple or spring.
 *
 * @param props - See {@link PiDotPopProps}.
 * @return The pop layer, or null before the anchor glyph has been measured.
 * @throws Never.
 */
export function PiDotPop({
  containerRef,
  color = PI_CARDINAL,
  onClick,
  onSettled,
}: PiDotPopProps) {
  const [anchor, setAnchor] = useState<DotAnchor>();
  const [phase, setPhase] = useState<PopPhase>('waiting');
  // The "i"'s own char span — clipped (tittle hidden) when the π takes over.
  const dotSpanRef = useRef<HTMLSpanElement>(null);
  const [reducedMotion] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  useEffect(() => {
    const container = containerRef.current;
    if (container === null || typeof ResizeObserver === 'undefined') {
      return;
    }
    const measure = () => {
      // Reassemble the title from the settled glyph spans and index straight
      // to the dotted "i" — robust to the words around it changing, and to
      // however many other "i"s the title contains.
      const spans = Array.from(
        container.querySelectorAll<HTMLSpanElement>('span[data-ch]'),
      );
      const text = spans.map(span => span.dataset.ch ?? '').join('');
      const wordStart = text.indexOf(ANCHOR_WORD);
      if (wordStart === -1) {
        return;
      }
      const dotSpan = spans[wordStart + ANCHOR_CHAR_OFFSET];
      if (dotSpan === undefined) {
        return;
      }
      dotSpanRef.current = dotSpan;
      const containerRect = container.getBoundingClientRect();
      const rect = dotSpan.getBoundingClientRect();
      setAnchor({
        xPx: rect.left - containerRect.left + rect.width / 2,
        yPx: rect.top - containerRect.top + rect.height * DOT_CENTER_FRACTION,
        charHeightPx: rect.height,
      });
    };
    measure();
    // Re-anchor when the hero reflows (the title's font-size is
    // viewport-clamped, so resize moves the dot).
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [containerRef]);

  useEffect(() => {
    if (reducedMotion) {
      // No pop animation to play, so there's no separate "popping" phase —
      // go straight to settled.
      setPhase('settled');
      return;
    }
    const timeoutId = window.setTimeout(() => {
      setPhase('popping');
    }, POP_DELAY_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [reducedMotion]);

  useEffect(() => {
    if (phase === 'settled') {
      onSettled?.();
    }
    // onSettled is intentionally excluded: firing again if the caller passes
    // a new function reference would break the "never more than once per
    // mount" contract documented on the prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // The moment the pop starts, the glyph's own tittle disappears — the π *is*
  // the dot from here on, through every later phase (`popping`, `settled`).
  // Imperatively styles a span HeroTitleMorph rendered (its settled DOM is
  // static, so React never reconciles this away); undone only on unmount so
  // a remount can replay cleanly — NOT between phases, or the dot would pop
  // back in behind the π when the pop hands off to the idle bounce.
  useEffect(() => {
    const dotSpan = dotSpanRef.current;
    if (phase === 'waiting' || dotSpan === null) {
      return;
    }
    dotSpan.style.clipPath = DOT_CLIP_PATH;
    return () => {
      dotSpan.style.clipPath = '';
    };
  }, [phase]);

  if (anchor === undefined || phase === 'waiting') {
    return null;
  }

  const clickable = onClick !== undefined;
  // Shared across both render paths below: as a plain decoration the layer is
  // aria-hidden; as a replay button it joins the accessibility tree instead.
  const layerA11yProps = clickable
    ? {
        as: 'button' as const,
        type: 'button' as const,
        onClick,
        'aria-label': 'Replay the intro animation',
      }
    : {'aria-hidden': true};

  const piWidthPx = anchor.charHeightPx * PI_SIZE_FRACTION;
  const piHeightPx = piWidthPx * PI_ASPECT;
  // The π rests as the new tittle: its bottom sits just below where the old
  // dot's bottom edge was, so it reads as part of the letter without lying
  // over the stem.
  const restCenterYPx =
    anchor.yPx +
    anchor.charHeightPx * REST_BOTTOM_OFFSET_FRACTION -
    piHeightPx / 2;
  // Spring travel: the π starts scale-0 centered on the dot itself.
  const risePx = anchor.yPx - restCenterYPx;
  const idleBouncePx = Math.max(
    1.5,
    anchor.charHeightPx * IDLE_BOUNCE_FRACTION,
  );

  const layerStyle = {
    left: anchor.xPx - piWidthPx / 2,
    top: restCenterYPx - piHeightPx / 2,
    width: piWidthPx,
    height: piHeightPx,
  };

  if (reducedMotion) {
    return (
      <PopLayer style={layerStyle} $clickable={clickable} {...layerA11yProps}>
        <PiMark draw={false} size={piWidthPx} color={color} />
      </PopLayer>
    );
  }

  return (
    <PopLayer style={layerStyle} $clickable={clickable} {...layerA11yProps}>
      {/* Outer layer: the *travel* — rises out of the dot on a lively,
          underdamped spring (a real bounce: it overshoots the resting spot
          and rebounds a couple of times before settling), with a slight tilt
          that un-rights itself on a back-out curve. Once the spring has
          settled, it hands off to an ever-so-slight perpetual idle bounce. */}
      <motion.div
        initial={{y: risePx, rotate: -14, opacity: 0}}
        animate={
          phase === 'settled'
            ? {y: [0, -idleBouncePx, 0], rotate: 0, opacity: 1}
            : {y: 0, rotate: 0, opacity: 1}
        }
        transition={
          phase === 'settled'
            ? {
                y: {
                  duration: IDLE_BOUNCE_PERIOD_SEC,
                  repeat: Infinity,
                  ease: 'easeInOut',
                },
              }
            : {
                y: {type: 'spring', stiffness: 300, damping: 9, mass: 0.9},
                rotate: {duration: 0.7, ease: [0.34, 1.56, 0.64, 1]},
                opacity: {duration: 0.1, ease: 'linear'},
              }
        }
        onAnimationComplete={() => {
          if (phase === 'popping') {
            setPhase('settled');
          }
        }}
      >
        {/* Inner layer: the *body* — classic squash-and-stretch, phased to
            the travel: launches stretched tall and narrow, squashes wide at
            the top of the overshoot, relaxes to rest. Bottom-weighted origin
            so the stretch reads as launching up out of the dot. */}
        <motion.div
          initial={{scaleX: 0, scaleY: 0}}
          animate={{scaleX: [0, 0.76, 1.1, 1], scaleY: [0, 1.18, 0.86, 1]}}
          transition={{
            duration: 0.8,
            times: [0, 0.35, 0.66, 1],
            ease: 'easeOut',
          }}
          style={{transformOrigin: '50% 90%'}}
        >
          <PiMark draw={false} size={piWidthPx} color={color} />
        </motion.div>
      </motion.div>
    </PopLayer>
  );
}
